import React, { useEffect, useState } from "react";
import {
  getHorariosClientesAgrupados,
  eliminarHorariosEspecificos,
} from "../../componentes/http/actions/HorarioClienteAction";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteIcon from "@mui/icons-material/Delete";
import CrearHorarioClienteModal from "../../componentes/modal/Clientes/CrearHorarioClienteModal";

const GestorHorariosClientes = () => {
  const [horarios, setHorarios] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [horarioSeleccionado, setHorarioSeleccionado] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    horario: null
  });
  // Mapeo de números de día a nombres
  const diasSemana = {
    1: 'Lunes',
    2: 'Martes',
    3: 'Miércoles',
    4: 'Jueves',
    5: 'Viernes',
    6: 'Sábado',
    7: 'Domingo'
  };

  useEffect(() => {
    fetchHorarios();
  }, []);

  const fetchHorarios = () => {
    setIsLoading(true);
    getHorariosClientesAgrupados(localStorage.getItem("token"))
      .then((response) => {
        setHorarios(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        console.log(error);
        setHorarios([]); 
        setSnackbar({
          open: true,
          message: "Error al cargar los horarios de clientes",
          severity: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
        setInitialDataLoaded(true);
      });
  };
  
  // Modal de nuevo horario
  const handleOpenModal = () => {
    setHorarioSeleccionado(null);
    setOpenModal(true);
  };
  
  const handleCloseModal = () => setOpenModal(false);
  
  // Abrir modal para editar
  const handleOpenEditModal = (horario) => {
    setHorarioSeleccionado({
      horarioIds: horario.horarioIds,
      clienteId: horario.clienteId,
      nombreCliente: horario.nombreCliente,
      dias: horario.diasEnvio,
      horaEnvio: horario.horaEnvio
    });
    setOpenModal(true);
  };

  // Formatear hora para mostrar
  const formatearHora = (horaString) => {
    if (!horaString) return '--:--';
    // Si horaString es un objeto TimeSpan formateado como string, extraer solo HH:mm
    return horaString.substring(0, 5);
  };

  // Eliminar horario de cliente
  const handleEliminarHorario = (horario) => {
    setDeleteDialog({
      open: true,
      horario: horario // Cambiar para pasar todo el horario
    });
  };

  // Confirmar eliminación desde el dialog
const confirmDelete = () => {
  setIsLoading(true);
  
  // Usar la nueva función para eliminar horarios específicos
  eliminarHorariosEspecificos(localStorage.getItem("token"), deleteDialog.horario.horarioIds)
    .then(() => {
      fetchHorarios();
      setSnackbar({
        open: true,
        message: "Horario eliminado con éxito",
        severity: "success",
      });
    })
    .catch((error) => {
      console.error("Error al eliminar horario:", error);
      setSnackbar({
        open: true,
        message: "Error al eliminar el horario",
        severity: "error",
      });
    })
    .finally(() => {
      setIsLoading(false);
      setDeleteDialog({ open: false, horario: null });
    });
};
  
  // Cerrar dialog sin eliminar
  const cancelDelete = () => {
    setDeleteDialog({ open: false, horario: null });
  };
  const handleGuardarHorario = async (horarioData) => {
    setIsLoading(true);
    
    try {
      await fetchHorarios();
      handleCloseModal();
      setSnackbar({
        open: true,
        message: horarioSeleccionado 
          ? "Horario actualizado con éxito" 
          : "Horario creado con éxito",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al guardar horario:", error);
      setSnackbar({
        open: true,
        message: "Error al guardar el horario",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <Grid container direction="column" spacing={2} sx={{paddingTop: {xs: '20px', sm: '10px'}}}>
        <Grid item>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h4">Horarios de Clientes</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenModal}
              disabled={isLoading}
            >
              Configurar Nuevo Horario
            </Button>
          </Grid>
        </Grid>

        <Grid item>
          <TableContainer component={Paper}>
            {isLoading && !initialDataLoaded ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : horarios.length > 0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Días de Envío</TableCell>
                    <TableCell>Hora de Envío</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {horarios.map((horario, index) => (
                    <TableRow key={`${horario.clienteId}-${index}`}>
                      <TableCell>{horario.nombreCliente}</TableCell>
                      <TableCell>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {horario.diasEnvio.map(dia => (
                            <Chip 
                              key={dia} 
                              label={diasSemana[dia]} 
                              size="small" 
                              color="primary" 
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{formatearHora(horario.horaEnvio)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                          <Tooltip title="Editar horario">
                            <IconButton 
                              color="primary" 
                              onClick={() => handleOpenEditModal(horario)}
                            >
                              <EditNoteIcon />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Eliminar horario">
                            <IconButton 
                              color="error" 
                              onClick={() => handleEliminarHorario(horario)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Box p={3}>
                <Typography>
                  No se encontraron horarios configurados para los clientes.
                </Typography>
              </Box>
            )}
          </TableContainer>
        </Grid>

        <CrearHorarioClienteModal
          open={openModal}
          onClose={handleCloseModal}
          onSubmit={handleGuardarHorario}
          horarioEditar={horarioSeleccionado}
        />
        <Dialog
        open={deleteDialog.open}
        onClose={cancelDelete}
        >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
            <DialogContentText>
            ¿Está seguro que desea eliminar este horario? Esta acción no se puede deshacer.
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={cancelDelete} color="primary">
            Cancelar
            </Button>
            <Button onClick={confirmDelete} color="error" autoFocus>
            Eliminar
            </Button>
        </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Grid>
  );
};

export default GestorHorariosClientes;